import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Button } from '@noths/polaris-design-system-core-components';

import {
  ADD_TO_BASKET_CTA,
  ADDING_TO_BASKET_CTA,
  BUY_GIFT_CARD_CTA,
  COMPLETE_REQUIRED_FIELDS_COPY,
  EDIT_PERSONALISATION_CTA,
  EDIT_YOUR_OPTIONS_CTA,
  PERSONALISE_AND_BUY_CTA,
  PICK_YOUR_OPTIONS_CTA,
  SAVING_PERSONALISATION_CTA,
} from './copy';
import { MAIN_PAGE_CTA_ID } from './elementIDs';
import * as styles from './BasketCTAButton.styles';

export interface BasketCTAButtonDispatchProps {
  onAddEditClick: VoidFunction;
  onBuyGiftCard: (ev: React.MouseEvent) => void;
  onVisible: () => void;
}

export interface BasketCTAButtonStateProps {
  editMode: boolean;
  hasOptions: boolean;
  loading: boolean;
  personalisable: boolean;
  purchasable: boolean;
  requiredFieldsIncomplete: boolean;
}

export interface BasketCTAButtonOwnProps {
  /**
   * Set on the CTA rendered inside the options drawer, where the button performs
   * the actual basket add and so always reads "Add to basket". The page CTA
   * (default) instead opens the drawer and switches copy when options exist.
   */
  isDrawerCta?: boolean;
}

type BasketCTAButtonProps = BasketCTAButtonDispatchProps &
  BasketCTAButtonStateProps &
  BasketCTAButtonOwnProps;

export const BasketCTAButton = ({
  editMode,
  hasOptions,
  isDrawerCta = false,
  loading,
  onAddEditClick,
  onBuyGiftCard,
  onVisible,
  personalisable,
  purchasable,
  requiredFieldsIncomplete,
}: BasketCTAButtonProps) => {
  const rootRef = useImpressionRef<HTMLDivElement>(() => onVisible());
  // Only the in-drawer CTA performs the basket add, so it is the only one gated on
  // required options being complete. The page CTA just opens the drawer.
  const disabledForRequiredFields = isDrawerCta && requiredFieldsIncomplete;
  const addCTAText =
    !isDrawerCta && hasOptions
      ? personalisable
        ? PERSONALISE_AND_BUY_CTA
        : PICK_YOUR_OPTIONS_CTA
      : ADD_TO_BASKET_CTA;
  // In edit mode the drawer CTA saves the amended options, while the page CTA
  // re-opens the drawer to edit them.
  const editCTAText = isDrawerCta ? EDIT_PERSONALISATION_CTA : EDIT_YOUR_OPTIONS_CTA;
  const purchasableCTAText = loading
    ? editMode
      ? SAVING_PERSONALISATION_CTA
      : ADDING_TO_BASKET_CTA
    : editMode
    ? editCTAText
    : addCTAText;

  return (
    <div css={[styles.ctaSectionWrapper]} ref={rootRef}>
      {purchasable ? (
        <div css={styles.purchasableCta}>
          <Button
            disabled={loading || disabledForRequiredFields}
            fullWidth
            id={MAIN_PAGE_CTA_ID}
            onClick={() => onAddEditClick()}
            type="button"
          >
            {purchasableCTAText}
          </Button>
          {disabledForRequiredFields && (
            <p css={styles.requiredFieldsMessage}>{COMPLETE_REQUIRED_FIELDS_COPY}</p>
          )}
        </div>
      ) : (
        <Button fullWidth href="/gift-cards" id={MAIN_PAGE_CTA_ID} onClick={onBuyGiftCard}>
          {BUY_GIFT_CARD_CTA}
        </Button>
      )}
    </div>
  );
};
